import { forwardRef } from 'react';

import round from 'lodash/round';

import {
  Column,
  Drawer,
  DrawerProps,
  TableHeader,
  Typography,
  Stack,
} from '@cast/design-system';
import { WorkloadGpuUtilization } from '@cast/types';
import { toBig } from '@cast/utils';

import { CloseDrawerButton, DrawerContent } from 'components/drawer/ds';
import { tableCacheKeys } from 'constants/tableCacheKeys';
import { useWorkloadsGpuUtilizationQuery } from 'hooks/queries/cost';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { FailedToLoad } from '@cast/shared-messages-ui';

import {
  SearchProvider,
  SearchableTable,
  UseServerData,
  useSearchContext,
  Search,
  NoData,
  NoResults,
  SearchableTableRecordsCount,
} from '@cast/shared-search-ui';

const useGpuWorkloads: UseServerData = ({ sortingState, freeText }) => {
  const { cluster } = useCluster();

  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
    count,
    isFetching,
  } = useWorkloadsGpuUtilizationQuery({
    clusterIds: [cluster.id],
    sortBy: sortingState?.columnId,
    sortDirection: sortingState?.sortBy.direction,
    search: freeText,
  });

  return {
    data,
    isLoading,
    isFetching,
    hasNextPage: !isLoading && hasNextPage,
    fetchNextPage,
    hasPreviousPage: !isLoading,
    error,
    refetch,
    metadata: { count: count || 0 },
  };
};

const GpuWorkloadsDrawerContent = () => {
  const searchCtx = useSearchContext<WorkloadGpuUtilization>();

  return (
    <DrawerContent mt={64} mb={8} gap={16}>
      <Typography variant="h5" pb={8}>
        Workloads actively using GPU
      </Typography>
      <Search disableRecentSearch />
      <Stack mx={-32} flexGrow={1}>
        <SearchableTable
          cacheKey={tableCacheKeys.CLUSTER_DASHBOARD.WORKLOADS_GPU_UTILIZATION}
          urlKey={tableCacheKeys.CLUSTER_DASHBOARD.WORKLOADS_GPU_UTILIZATION}
          testId={tableCacheKeys.CLUSTER_DASHBOARD.WORKLOADS_GPU_UTILIZATION}
          maxHeight="calc(100vh - 164px)"
          rowKey={(row: WorkloadGpuUtilization) =>
            `${row.workloadName}-${row.workloadType}-${row.namespace}`
          }
          sizing="m"
          components={{
            noData: <NoData entity="workload" />,
            noResults: <NoResults entity="workload" />,
            failed: (
              <FailedToLoad
                title="Failed to load workloads gpu utilization"
                refresh={searchCtx?.refetch}
              />
            ),
          }}
          outerHeader={
            <TableHeader
              recordsCount={
                <SearchableTableRecordsCount
                  title="workload"
                  outOf={searchCtx?.metadata?.count}
                />
              }
              px={32}
            />
          }
        >
          <Column<WorkloadGpuUtilization>
            id="workloadName"
            header="Workload"
            minWidth={186}
            cellWrapperSx={{ pl: 32 }}
            sortable
          />
          <Column<WorkloadGpuUtilization>
            id="workloadType"
            header="Type"
            minWidth={104}
            sortable
          />
          <Column<WorkloadGpuUtilization>
            id="utilizationPercent"
            header="Avg. GPU Utiliz."
            headerFullName="Average GPU utilization"
            renderCell={({ utilizationPercent }) =>
              `${round(toBig(utilizationPercent || 0).toNumber(), 2)}%`
            }
            minWidth={144}
            sortable
            sortingType="number"
          />
        </SearchableTable>
      </Stack>
    </DrawerContent>
  );
};

export const GpuWorkloadsDrawer = forwardRef<HTMLDivElement, DrawerProps>(
  (drawerProps, ref) => {
    return (
      <Drawer
        {...drawerProps}
        ref={ref}
        size="small"
        hideBackdrop={false}
        testId="gpu-workloads-drawer"
      >
        <SearchProvider
          urlKey={tableCacheKeys.CLUSTER_DASHBOARD.WORKLOADS_GPU_UTILIZATION}
          useData={useGpuWorkloads}
          serverSideSort
          freeTextServerSearch
          freeTextDebounceDelay={500}
        >
          <CloseDrawerButton />
          <GpuWorkloadsDrawerContent />
        </SearchProvider>
      </Drawer>
    );
  }
);

GpuWorkloadsDrawer.displayName = 'GpuWorkloadsDrawer';
